import { Check, Pencil } from "lucide-react";
import React from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useApp } from "@/contexts/AppContext";
import { ONBOARDING_STEPS, useOnboarding } from "@/contexts/OnboardingContext";
import { getForecastTerm } from "@/lib/terminology-config";
import { cn } from "@/lib/utils";

// ── Setup summary ─────────────────────────────────────────────────────────────
// Reachable at any time via "Review Setup". Editing a step navigates inside the
// assistant — it never closes the assistant, changes a status, or completes
// onboarding.

export const CompletionScreen: React.FC = () => {
  const { handleNavigate } = useApp(); // Global navigate — wired from AppContent
  const { completeOnboarding, goToStep, prevStep, getStepStatus } =
    useOnboarding();

  // Confirm force-completes onboarding: outstanding steps are recorded as skipped.
  const handleConfirm = () => {
    const skippedNames = ONBOARDING_STEPS.filter(
      (s) => getStepStatus(s.id) === "Skipped",
    ).map((s) => s.name);
    const neverStartedNames = ONBOARDING_STEPS.filter((s) => {
      const status = getStepStatus(s.id);
      return status === "NotStarted" || status === "Started";
    }).map((s) => s.name);
    const outstanding = skippedNames.length + neverStartedNames.length;

    completeOnboarding();
    handleNavigate("admin-dashboard");

    if (outstanding > 0) {
      const parts: string[] = [];
      if (skippedNames.length > 0) {
        parts.push(`Skipped: ${skippedNames.join(", ")}.`);
      }
      if (neverStartedNames.length > 0) {
        parts.push(`Never started: ${neverStartedNames.join(", ")}.`);
      }

      toast.info(
        `Setup complete — ${outstanding} step${outstanding > 1 ? "s" : ""} not configured`,
        {
          description: `${parts.join(" ")} Visit the relevant Admin screens to configure these manually when you're ready.`,
          duration: 8000,
        },
      );
    }
  };

  return (
    <div className="p-8 min-h-full">
      {/* Header */}
      <div className="flex flex-col items-center text-center mb-10">
        <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-4">
          <Check className="h-8 w-8 text-green-600" strokeWidth={2.5} />
        </div>
        <h2 className="text-2xl font-bold text-foreground">Setup summary</h2>
        <p className="text-muted-foreground mt-1 text-sm">
          Review what has been configured. Nothing is completed until you
          confirm.
        </p>
      </div>

      <div className="max-w-4xl mx-auto mb-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={prevStep}
          className="text-muted-foreground"
        >
          ← Back to steps
        </Button>
      </div>

      {/* Two-column layout */}
      <div className="grid grid-cols-2 gap-8 max-w-4xl mx-auto">
        {/* LEFT — Setup Summary */}
        <div>
          <h3 className="text-sm font-semibold text-foreground mb-3 uppercase tracking-wide">
            Setup Summary
          </h3>
          <div className="space-y-2">
            {ONBOARDING_STEPS.map((step) => {
              const status = getStepStatus(step.id);
              const isSkipped = status === "Skipped";
              const isCompleted = status === "Completed";

              return (
                <div
                  key={step.id}
                  className="flex items-center justify-between gap-3 p-3 rounded-lg border border-border bg-white"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    {/* Status indicator dot */}
                    <div
                      className={cn(
                        "w-2 h-2 rounded-full flex-shrink-0",
                        isCompleted && "bg-green-500",
                        isSkipped && "bg-amber-400",
                        !isCompleted && !isSkipped && "bg-muted-foreground/30",
                      )}
                    />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">
                        {step.name}
                      </p>
                      <p
                        className={cn(
                          "text-xs",
                          isCompleted && "text-green-600",
                          isSkipped && "text-amber-600",
                          !isCompleted && !isSkipped && "text-muted-foreground",
                        )}
                      >
                        {isCompleted
                          ? "Completed"
                          : isSkipped
                            ? "Skipped"
                            : status === "Started"
                              ? "In progress"
                              : "Not configured"}
                      </p>
                    </div>
                  </div>

                  {/* Edit — navigates to the step inside the assistant */}
                  <button
                    type="button"
                    onClick={() => goToStep(step.number)}
                    className="flex items-center gap-1 text-xs text-primary hover:text-primary/80 hover:underline underline-offset-2 transition-colors flex-shrink-0"
                  >
                    Edit
                    <Pencil className="h-3 w-3" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {/* RIGHT — What to do next */}
        <div>
          <h3 className="text-sm font-semibold text-foreground mb-3 uppercase tracking-wide">
            What to do next
          </h3>
          <ol className="space-y-2.5">
            {getWhatToDoNext().map((item, idx) => (
              <li key={idx} className="flex items-start gap-3">
                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary/10 text-primary text-[11px] font-semibold flex items-center justify-center mt-0.5">
                  {idx + 1}
                </span>
                <p className="text-sm text-muted-foreground leading-snug">
                  {item}
                </p>
              </li>
            ))}
          </ol>
        </div>
      </div>

      {/* CTA */}
      <div className="flex flex-col items-center gap-3 mt-10">
        <Button size="lg" onClick={handleConfirm} className="px-8">
          Confirm Onboarding Complete →
        </Button>
      </div>
    </div>
  );
};

// ── What to do next items ─────────────────────────────────────────────────────
// Uses getForecastTerm() so the correct configured term is always shown.

function getWhatToDoNext(): string[] {
  const forecastTerm = getForecastTerm(false); // e.g. "forecast" / "projection"
  return [
    `Review your ${forecastTerm} baseline on the main dashboard`,
    `Connect additional data sources to improve ${forecastTerm} accuracy`,
    "Invite your finance team and assign them the right roles",
    "Configure child categories if needed (Settings → Parent Child Categories)",
    "Customise terminology to match your organisation (Settings → Terminology)",
  ];
}
